// src/components/AIAgent/AIAgent.js

import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FaRobot, FaCog, FaComments, FaPlus, FaSave, FaEye, FaEyeSlash, FaBox, FaCogs, FaBook, FaChartBar, FaExclamationTriangle } from "react-icons/fa";
import { useAIAgentStore } from "../../services/useAIAgentStore";
import ProductCatalog from "./ProductCatalog";
import ServiceCatalog from "./ServiceCatalog";
import PlaybookEditor from "./PlaybookEditor";
import ConversationDetail from "./ConversationDetail";
import TokenUsageIndicator from "./TokenUsageIndicator";
import MetricsDashboard from "./MetricsDashboard";
import EscalationReview from "./EscalationReview";
import AgentHealthStatus from "./AgentHealthStatus";
import usePaymentGuard from "../../hooks/usePaymentGuard";
import PaymentBlockModal from "../PaymentBlockModal/PaymentBlockModal";
import "./AIAgent.css";

const AIAgent = () => {
  const {
    profile,
    conversations,
    workspaceMetrics,
    loading,
    error,
    fetchProfile,
    createProfile,
    updateProfile,
    fetchConversations,
    fetchWorkspaceMetrics,
    validateApiKey,
    clearError,
    markConversationReviewed,
  } = useAIAgentStore();

  // Hook para verificar se pode executar ações (requer pagamento)
  const {
    needsPayment,
    showBlockModal,
    closeBlockModal,
    showPaymentRequired,
  } = usePaymentGuard();

  const [activeTab, setActiveTab] = useState("config");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [conversationStatusFilter, setConversationStatusFilter] = useState("");
  const [formData, setFormData] = useState({
    name: "Assistente Virtual",
    persona: {
      tone: "amigavel",
      formality: "voce",
      emoji_usage: "moderado",
      language: "pt-BR",
      custom_instructions: "",
    },
    rules: {
      can_discuss_price: true,
      can_schedule: true,
      escalation_keywords: [],
      forbidden_topics: [],
      custom_rules: "",
    },
    openai: {
      model: "gpt-4o",
      max_tokens: 500,
      temperature: 0.7,
      api_key: "",
    },
  });

  const [escalationKeyword, setEscalationKeyword] = useState("");
  const [forbiddenTopic, setForbiddenTopic] = useState("");

  useEffect(() => {
    fetchProfile();
    fetchConversations();
    fetchWorkspaceMetrics();
  }, []);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || "Assistente Virtual",
        persona: {
          tone: profile.persona?.tone || "amigavel",
          formality: profile.persona?.formality || "voce",
          emoji_usage: profile.persona?.emoji_usage || "moderado",
          language: profile.persona?.language || "pt-BR",
          custom_instructions: profile.persona?.custom_instructions || "",
        },
        rules: {
          can_discuss_price: profile.rules?.can_discuss_price ?? true,
          can_schedule: profile.rules?.can_schedule ?? true,
          escalation_keywords: profile.rules?.escalation_keywords || [],
          forbidden_topics: profile.rules?.forbidden_topics || [],
          custom_rules: profile.rules?.custom_rules || "",
        },
        openai: {
          model: profile.openai?.model || "gpt-4o",
          max_tokens: profile.openai?.max_tokens || 500,
          temperature: profile.openai?.temperature || 0.7,
          api_key: "",
        },
      });
    }
  }, [profile]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error]);

  const handleInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleAddEscalationKeyword = () => {
    if (escalationKeyword.trim()) {
      setFormData((prev) => ({
        ...prev,
        rules: {
          ...prev.rules,
          escalation_keywords: [...prev.rules.escalation_keywords, escalationKeyword.trim()],
        },
      }));
      setEscalationKeyword("");
    }
  };

  const handleRemoveEscalationKeyword = (keyword) => {
    setFormData((prev) => ({
      ...prev,
      rules: {
        ...prev.rules,
        escalation_keywords: prev.rules.escalation_keywords.filter((k) => k !== keyword),
      },
    }));
  };

  const handleAddForbiddenTopic = () => {
    if (forbiddenTopic.trim()) {
      setFormData((prev) => ({
        ...prev,
        rules: {
          ...prev.rules,
          forbidden_topics: [...prev.rules.forbidden_topics, forbiddenTopic.trim()],
        },
      }));
      setForbiddenTopic("");
    }
  };

  const handleRemoveForbiddenTopic = (topic) => {
    setFormData((prev) => ({
      ...prev,
      rules: {
        ...prev.rules,
        forbidden_topics: prev.rules.forbidden_topics.filter((t) => t !== topic),
      },
    }));
  };

  const handleSave = async () => {
    // Verificar se precisa de pagamento antes de permitir salvar
    if (needsPayment) {
      showPaymentRequired();
      return;
    }

    try {

      const dataToSave = { ...formData };
            console.warn(dataToSave);
      console.log("======================================================================================")

      // Only include API key if it was changed
      if (!dataToSave.openai.api_key) {
        delete dataToSave.openai.api_key;
      }

      if (profile) {
        await updateProfile(dataToSave);
        toast.success("Agente IA atualizado com sucesso!");
      } else {
        await createProfile(dataToSave);
        toast.success("Agente IA criado com sucesso!");
      }
      setIsEditing(false);
    } catch (err) {
      // Error handled by store
    }
  };

  const handleValidateApiKey = async () => {
    if (!formData.openai.api_key) {
      toast.warning("Digite uma API key para validar");
      return;
    }

    const isValid = await validateApiKey(formData.openai.api_key);
    if (isValid) {
      toast.success("API key válida!");
    } else {
      toast.error("API key inválida ou expirada");
    }
  };

  const renderConfigTab = () => (
    <div className="ai-agent-config">
      {/* Agent Name */}
      <section className="config-section">
        <h3>Nome do Agente</h3>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: Assistente de Vendas"
          className="config-input"
        />
      </section>

      {/* Persona Configuration */}
      <section className="config-section">
        <h3>Persona</h3>
        <p className="section-description">Configure como o agente se comunica</p>

        <div className="config-grid">
          <div className="config-field">
            <label>Tom da Comunicação</label>
            <select
              value={formData.persona.tone}
              onChange={(e) => handleInputChange("persona", "tone", e.target.value)}
            >
              <option value="formal">Formal</option>
              <option value="informal">Informal</option>
              <option value="amigavel">Amigável</option>
              <option value="tecnico">Técnico</option>
            </select>
          </div>

          <div className="config-field">
            <label>Tratamento</label>
            <select
              value={formData.persona.formality}
              onChange={(e) => handleInputChange("persona", "formality", e.target.value)}
            >
              <option value="voce">Você</option>
              <option value="senhor">Senhor(a)</option>
              <option value="tu">Tu</option>
            </select>
          </div>

          <div className="config-field">
            <label>Uso de Emojis</label>
            <select
              value={formData.persona.emoji_usage}
              onChange={(e) => handleInputChange("persona", "emoji_usage", e.target.value)}
            >
              <option value="nunca">Nunca</option>
              <option value="moderado">Moderado</option>
              <option value="frequente">Frequente</option>
            </select>
          </div>

          <div className="config-field">
            <label>Idioma</label>
            <select
              value={formData.persona.language}
              onChange={(e) => handleInputChange("persona", "language", e.target.value)}
            >
              <option value="pt-BR">Português (Brasil)</option>
              <option value="en-US">English (US)</option>
              <option value="es">Español</option>
            </select>
          </div>
        </div>

        <div className="config-field full-width">
          <label>Instruções Personalizadas</label>
          <textarea
            value={formData.persona.custom_instructions}
            onChange={(e) => handleInputChange("persona", "custom_instructions", e.target.value)}
            placeholder="Ex: Sempre mencione que temos frete grátis acima de R$100..."
            rows={3}
          />
        </div>
      </section>

      {/* Rules Configuration */}
      <section className="config-section">
        <h3>Regras de Atendimento</h3>

        <div className="config-grid">
          <div className="config-field checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={formData.rules.can_discuss_price}
                onChange={(e) => handleInputChange("rules", "can_discuss_price", e.target.checked)}
              />
              Pode discutir preços
            </label>
          </div>

          <div className="config-field checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={formData.rules.can_schedule}
                onChange={(e) => handleInputChange("rules", "can_schedule", e.target.checked)}
              />
              Pode agendar reuniões
            </label>
          </div>
        </div>

        <div className="config-field">
          <label>Palavras de Escalação</label>
          <p className="field-hint">Quando detectadas, transfere para atendente humano</p>
          <div className="tag-input-container">
            <input
              type="text"
              value={escalationKeyword}
              onChange={(e) => setEscalationKeyword(e.target.value)}
              placeholder="Ex: falar com gerente"
              onKeyPress={(e) => e.key === "Enter" && handleAddEscalationKeyword()}
            />
            <button type="button" className="buttonAdicionar" onClick={handleAddEscalationKeyword}>
              Adicionar
            </button>
          </div>
          <div className="tags-list">
            {formData.rules.escalation_keywords.map((keyword) => (
              <span key={keyword} className="tag">
                {keyword}
                <button onClick={() => handleRemoveEscalationKeyword(keyword)}>&times;</button>
              </span>
            ))}
          </div>
        </div>

        <div className="config-field">
          <label>Tópicos Proibidos</label>
          <div className="tag-input-container">
            <input
              type="text"
              value={forbiddenTopic}
              onChange={(e) => setForbiddenTopic(e.target.value)}
              placeholder="Ex: concorrentes"
              onKeyPress={(e) => e.key === "Enter" && handleAddForbiddenTopic()}
            />
            <button type="button" className="buttonAdicionar" onClick={handleAddForbiddenTopic}>
              Adicionar
            </button>
          </div>
          <div className="tags-list">
            {formData.rules.forbidden_topics.map((topic) => (
              <span key={topic} className="tag">
                {topic}
                <button onClick={() => handleRemoveForbiddenTopic(topic)}>&times;</button>
              </span>
            ))}
          </div>
        </div>

        <div className="config-field full-width">
          <label>Regras Adicionais</label>
          <textarea
            value={formData.rules.custom_rules}
            onChange={(e) => handleInputChange("rules", "custom_rules", e.target.value)}
            placeholder="Ex: Sempre confirme o email antes de finalizar..."
            rows={3}
          />
        </div>
      </section>

      {/* OpenAI Configuration */}
      <section className={`config-section ${!profile?.openai?.has_api_key ? 'highlight-required' : ''}`}>
        <h3>
          Configuração OpenAI
          {!profile?.openai?.has_api_key && (
            <span className="required-badge">Obrigatório</span>
          )}
        </h3>
        {!profile?.openai?.has_api_key && (
          <div className="openai-setup-info">
            <p>
              <strong>A API Key da OpenAI é obrigatória</strong> para que o agente funcione.
              Você pode obter sua chave em{' '}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
                platform.openai.com/api-keys
              </a>
            </p>
          </div>
        )}

        <div className="config-grid">
          <div className="config-field">
            <label>Modelo</label>
            <select
              value={formData.openai.model}
              onChange={(e) => handleInputChange("openai", "model", e.target.value)}
            >
              <option value="gpt-4o">GPT-4o (Recomendado)</option>
              <option value="gpt-4o-mini">GPT-4o Mini (Econômico)</option>
              <option value="gpt-4-turbo">GPT-4 Turbo</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            </select>
          </div>

          <div className="config-field">
            <label>Max Tokens: {formData.openai.max_tokens}</label>
            <input
              type="range"
              min="100"
              max="2000"
              value={formData.openai.max_tokens}
              onChange={(e) => handleInputChange("openai", "max_tokens", parseInt(e.target.value))}
            />
          </div>

          <div className="config-field">
            <label>Temperatura: {formData.openai.temperature}</label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={formData.openai.temperature}
              onChange={(e) =>
                handleInputChange("openai", "temperature", parseFloat(e.target.value))
              }
            />
          </div>
        </div>

        <div className="config-field">
          <label>API Key OpenAI</label>
          <div className="api-key-input">
            <input
              type={showApiKey ? "text" : "password"}
              value={formData.openai.api_key}
              onChange={(e) => handleInputChange("openai", "api_key", e.target.value)}
              placeholder={profile?.openai?.has_api_key ? "••••••••••••••••" : "sk-..."}
            />
            <button
              type="button"
              className="icon-button"
              onClick={() => setShowApiKey(!showApiKey)}
            >
              {showApiKey ? <FaEyeSlash /> : <FaEye />}
            </button>
            <button type="button" className="validate-button" onClick={handleValidateApiKey}>
              Validar
            </button>
          </div>
          {profile?.openai?.has_api_key && (
            <p className="field-hint success">API key configurada. Deixe em branco para manter.</p>
          )}
        </div>
      </section>

    </div>
  );

  const [reactivatingId, setReactivatingId] = useState(null);

  const handleStatusFilterChange = (status) => {
    setConversationStatusFilter(status);
    fetchConversations(status || null);
  };

  const handleReactivateLead = async (e, conversationId) => {
    e.stopPropagation(); // Prevent opening conversation detail
    setReactivatingId(conversationId);
    try {
      await markConversationReviewed(conversationId);
      toast.success("Lead reativado! O agente IA voltara a responder as mensagens.");
      // Refresh conversations with current filter
      await fetchConversations(conversationStatusFilter || null);
    } catch (err) {
      console.error("Error reactivating lead:", err);
      toast.error("Erro ao reativar o lead. Tente novamente.");
    } finally {
      setReactivatingId(null);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: "Ativo",
      escalated: "Escalado",
      closed: "Fechado",
    };
    return labels[status] || status;
  };

  const renderConversationsTab = () => {
    // If a conversation is selected, show detail view
    if (selectedConversationId) {
      return (
        <ConversationDetail
          conversationId={selectedConversationId}
          onClose={() => setSelectedConversationId(null)}
        />
      );
    }

    return (
      <div className="ai-agent-conversations">
        <div className="conversations-header">
          <h3>Conversas</h3>
          <div className="conversations-filters">
            <label>Filtrar por status:</label>
            <select
              value={conversationStatusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="status-filter-select"
            >
              <option value="">Todas</option>
              <option value="active">Ativas (IA respondendo)</option>
              <option value="escalated">Escaladas (Aguardando humano)</option>
              <option value="closed">Fechadas</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">Carregando conversas...</div>
        ) : conversations.length === 0 ? (
          <div className="empty-state">
            <FaComments size={48} />
            <p>Nenhuma conversa {conversationStatusFilter ? `com status "${getStatusLabel(conversationStatusFilter)}"` : "ainda"}</p>
            <span>
              {conversationStatusFilter
                ? "Tente mudar o filtro de status."
                : "As conversas aparecerão aqui quando o agente começar a atender."}
            </span>
          </div>
        ) : (
          <div className="conversations-list">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`conversation-item status-${conv.status} clickable`}
                onClick={() => setSelectedConversationId(conv.id)}
              >
                <div className="conversation-header">
                  <span className="conversation-lead">Lead #{conv.lead_id?.substring(0, 8)}</span>
                  <span className={`conversation-status ${conv.status}`}>
                    {getStatusLabel(conv.status)}
                  </span>
                </div>
                <div className="conversation-meta">
                  <span>{conv.message_count} mensagens</span>
                  {conv.last_message_at && (
                    <span>Ultima: {new Date(conv.last_message_at).toLocaleString()}</span>
                  )}
                </div>
                {conv.status === "escalated" && (
                  <div className="escalation-info-row">
                    <div className="escalation-info-badge">
                      Aguardando revisao humana
                    </div>
                    <button
                      className="btn-reactivate-inline"
                      onClick={(e) => handleReactivateLead(e, conv.id)}
                      disabled={reactivatingId === conv.id}
                      title="Reativa o lead para o agente IA voltar a responder"
                    >
                      {reactivatingId === conv.id ? "Reativando..." : "Reativar Lead"}
                    </button>
                  </div>
                )}
                {conv.current_summary && (
                  <p className="conversation-summary">{conv.current_summary.substring(0, 150)}...</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const formatTokens = (tokens) => {
    if (!tokens) return "0";
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}k`;
    }
    return tokens.toString();
  };

  const renderMetricsTab = () => (
    <div className="ai-agent-metrics">
      <h3>Metricas do Agente IA</h3>

      {!workspaceMetrics ? (
        <div className="loading-state">Carregando metricas...</div>
      ) : (
        <div className="metrics-grid">
          {/* Conversations */}
          <div className="metrics-card">
            <h4>Conversas</h4>
            <div className="metrics-stats">
              <div className="metric-item">
                <span className="metric-value">{workspaceMetrics.conversations?.total || 0}</span>
                <span className="metric-label">Total</span>
              </div>
              <div className="metric-item">
                <span className="metric-value success">{workspaceMetrics.conversations?.active || 0}</span>
                <span className="metric-label">Ativas</span>
              </div>
              <div className="metric-item">
                <span className="metric-value warning">{workspaceMetrics.conversations?.escalated || 0}</span>
                <span className="metric-label">Escaladas</span>
              </div>
              <div className="metric-item">
                <span className="metric-value">{workspaceMetrics.conversations?.completed || 0}</span>
                <span className="metric-label">Finalizadas</span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="metrics-card">
            <h4>Mensagens</h4>
            <div className="metrics-stats">
              <div className="metric-item">
                <span className="metric-value">{workspaceMetrics.messages?.total || 0}</span>
                <span className="metric-label">Total</span>
              </div>
              <div className="metric-item">
                <span className="metric-value">{formatTokens(workspaceMetrics.messages?.prompt_tokens)}</span>
                <span className="metric-label">Tokens Prompt</span>
              </div>
              <div className="metric-item">
                <span className="metric-value">{formatTokens(workspaceMetrics.messages?.completion_tokens)}</span>
                <span className="metric-label">Tokens Resposta</span>
              </div>
              <div className="metric-item">
                <span className="metric-value">{workspaceMetrics.messages?.avg_latency_ms || 0}ms</span>
                <span className="metric-label">Latencia Media</span>
              </div>
            </div>
          </div>

          {/* Summaries */}
          <div className="metrics-card">
            <h4>Resumos</h4>
            <div className="metrics-stats">
              <div className="metric-item">
                <span className="metric-value">{workspaceMetrics.summaries?.total || 0}</span>
                <span className="metric-label">Total Gerados</span>
              </div>
              <div className="metric-item">
                <span className="metric-value success">{formatTokens(workspaceMetrics.summaries?.tokens_saved)}</span>
                <span className="metric-label">Tokens Salvos</span>
              </div>
              <div className="metric-item">
                <span className="metric-value">{workspaceMetrics.summaries?.messages_summarized || 0}</span>
                <span className="metric-label">Msgs Resumidas</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <button className="btn-refresh-metrics" onClick={fetchWorkspaceMetrics}>
        Atualizar Metricas
      </button>
    </div>
  );

  return (
    <div className="ai-agent-module">
      <header className="ai-agent-header">
        <div className="header-title">
          <FaRobot size={32} />
          <div>
            <h1>Agente de IA</h1>
            <p>Configure seu assistente virtual de vendas</p>
          </div>
        </div>

        <div className="header-right">
          {profile && <TokenUsageIndicator />}

          {profile && (
            <div className="agent-status">
              <span className={`status-badge ${profile.is_active ? "active" : "inactive"}`}>
                {profile.is_active ? "Ativo" : "Inativo"}
              </span>
            </div>
          )}
        </div>
      </header>

      {!profile && !loading && (
        <div className="setup-guide">
          <div className="setup-guide-header">
            <FaRobot className="setup-guide-icon" />
            <div>
              <h3>Vamos criar seu Agente de IA</h3>
              <p>Siga os passos abaixo para configurar o atendimento automático</p>
            </div>
          </div>

          <div className="setup-steps">
            <div className="setup-step">
              <span className="step-number">1</span>
              <span className="step-text">Defina o <strong>nome</strong> do seu agente</span>
            </div>
            <div className="setup-step">
              <span className="step-number">2</span>
              <span className="step-text">Configure a <strong>persona</strong> (tom de voz, tratamento)</span>
            </div>
            <div className="setup-step">
              <span className="step-number">3</span>
              <span className="step-text">Defina as <strong>regras</strong> de atendimento</span>
            </div>
            <div className="setup-step">
              <span className="step-number">4</span>
              <span className="step-text">Adicione sua <strong>API Key da OpenAI</strong></span>
            </div>
            <div className="setup-step completed">
              <span className="step-number">5</span>
              <span className="step-text">Clique em <strong>"Criar Agente IA"</strong> no final do formulário</span>
            </div>
          </div>

          <p className="setup-guide-footer">
            Preencha o formulário abaixo para começar
          </p>
        </div>
      )}

      {/* Alert when profile exists but no API key */}
      {profile && !profile.openai?.has_api_key && (
        <div className="api-key-alert">
          <div className="api-key-alert-content">
            <FaExclamationTriangle className="alert-icon" />
            <div className="alert-text">
              <strong>Configuração Obrigatória</strong>
              <p>Para o Agente de IA funcionar, você precisa configurar sua <strong>API Key da OpenAI</strong> na seção "Configuração OpenAI" abaixo.</p>
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
                Obter API Key da OpenAI →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Health Check Status - Mostra claramente o que impede o agente de funcionar */}
      {profile && <AgentHealthStatus autoRefresh={true} />}

      <nav className="ai-agent-tabs">
        <button
          className={`tab-button ${activeTab === "config" ? "active" : ""}`}
          onClick={() => setActiveTab("config")}
        >
          <FaCog /> Configuracoes
        </button>
        <button
          className={`tab-button ${activeTab === "products" ? "active" : ""}`}
          onClick={() => setActiveTab("products")}
          disabled={!profile}
        >
          <FaBox /> Produtos
        </button>
        <button
          className={`tab-button ${activeTab === "services" ? "active" : ""}`}
          onClick={() => setActiveTab("services")}
          disabled={!profile}
        >
          <FaCogs /> Servicos
        </button>
        <button
          className={`tab-button ${activeTab === "playbooks" ? "active" : ""}`}
          onClick={() => setActiveTab("playbooks")}
          disabled={!profile}
        >
          <FaBook /> Playbooks
        </button>
        <button
          className={`tab-button ${activeTab === "conversations" ? "active" : ""}`}
          onClick={() => { setActiveTab("conversations"); setSelectedConversationId(null); }}
        >
          <FaComments /> Conversas
        </button>
        <button
          className={`tab-button ${activeTab === "metrics" ? "active" : ""}`}
          onClick={() => setActiveTab("metrics")}
          disabled={!profile}
        >
          <FaChartBar /> Metricas
        </button>
        <button
          className={`tab-button ${activeTab === "escalations" ? "active" : ""}`}
          onClick={() => setActiveTab("escalations")}
          disabled={!profile}
        >
          <FaExclamationTriangle /> Escalacoes
        </button>
      </nav>

      <main className="ai-agent-content">
        {loading && !profile ? (
          <div className="loading-state">Carregando...</div>
        ) : (
          <>
            {activeTab === "config" && renderConfigTab()}
            {activeTab === "products" && profile && <ProductCatalog />}
            {activeTab === "services" && profile && <ServiceCatalog />}
            {activeTab === "playbooks" && profile && <PlaybookEditor />}
            {activeTab === "conversations" && renderConversationsTab()}
            {activeTab === "metrics" && profile && <MetricsDashboard />}
            {activeTab === "escalations" && profile && <EscalationReview />}
          </>
        )}
      </main>

      {/* Fixed Action Bar - Always visible when on config tab */}
      {activeTab === "config" && (
        <div className="fixed-action-bar">
          <div className="action-bar-content">
            <div className="action-bar-info">
              {!profile?.openai?.has_api_key && (
                <span className="action-bar-warning">
                  <FaExclamationTriangle /> API Key da OpenAI não configurada
                </span>
              )}
              {profile?.openai?.has_api_key && (
                <span className="action-bar-success">
                  <FaCog /> Agente configurado e pronto
                </span>
              )}
            </div>
            <button
              className={`action-bar-save-button ${!profile ? "create-mode" : ""}`}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                "Salvando..."
              ) : profile ? (
                <><FaSave /> Salvar Alterações</>
              ) : (
                <><FaPlus /> Criar Agente IA</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Modal de bloqueio por falta de pagamento */}
      <PaymentBlockModal
        isOpen={showBlockModal}
        onClose={closeBlockModal}
        actionName="configurar o Agente IA"
      />
    </div>
  );
};

export default AIAgent;
